import classNames from 'classnames';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import FlightSelectorSlideSection from './flight-selector-slide-section/flight-selector-slide-section';
import styles from './flight-selector-slide.module.scss';

type FlightSelectorTransport = Content.Components['FlightSelectorTransportDto'];

interface FlightSelectorSlideProps extends FlightSelectorTransport {
  /** The class name to apply */
  className?: string;
  /** Indicates if the slide is active */
  isActive?: boolean;
  /** Indicates if the price is relative */
  isPriceRelative?: boolean;
  /** The tooltip content to show when there is no time */
  noTimeHeadline: string;
  /** The text to show when there is no time */
  noTimeText: string;
  /** An event that is triggered on click */
  onClick: () => void;
}

const FlightSelectorSlide = ({
  className,
  homeboundFlightInformation,
  infoWarningText,
  isActive,
  infoWarningLink,
  isPriceRelative,
  noTimeText,
  outboundFlightInformation,
  totalPrice,
  onClick,
}: FlightSelectorSlideProps) => {
  const formattedPrice = useCurrencyFormatter()(totalPrice);

  const priceRelativeSymbol = isPriceRelative && totalPrice >= 0 ? '+' : '';

  const renderContent = (
    <React.Fragment>
      <Typography className={styles.price} tagStyle="priceSmall">
        {!isActive && `${priceRelativeSymbol}${formattedPrice}`}

        <Button
          className={styles.checkMark}
          icon={isActive ? 'CheckMark' : undefined}
          variant="tertiary"
        />
      </Typography>

      <FlightSelectorSlideSection
        {...outboundFlightInformation}
        noTimeText={noTimeText}
      />

      <div className={styles.divider} />

      <FlightSelectorSlideSection
        {...homeboundFlightInformation}
        noTimeText={noTimeText}
      />
    </React.Fragment>
  );

  if (isActive) {
    return (
      <div className={classNames(styles.component, className, styles.isActive)}>
        {renderContent}

        {infoWarningText && (
          <div className={styles.infoWarning}>
            <Icon className={styles.infoWarningIcon} name="Warning" />

            {infoWarningLink ? (
              <RichText
                className={styles.infoWarningText}
                text={`${infoWarningText} <a href="${infoWarningLink.url}" target="${infoWarningLink.target}">${infoWarningLink.text}</a>`}
              />
            ) : (
              <RichText
                className={styles.infoWarningText}
                text={`${infoWarningText}`}
              />
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      className={classNames(styles.component, className)}
      onClick={onClick}
    >
      {renderContent}
    </button>
  );
};

FlightSelectorSlide.displayName = 'FlightSelectorCard';

export default FlightSelectorSlide;
